// client/src/store/slices/authSlice.js
import { createSlice } from '@reduxjs/toolkit';
import {
  checkAuthStatus,
  login,
  register,
  logoutUser,
  diagnoseConnection,
  refreshSession,
  fetchAccess,
  emancipateCharacter,
  registerCharacterInAccess,
  fetchCharacters,
} from '../thunks/authThunks';

const initialState = {
  isAuthenticated: false,
  user: null,
  sessionId: null,
  token: null,
  accessToken: null,
  refreshToken: null,
  isLoading: false,
  error: null,
  authChecked: false,
  connectionStatus: null,
  isOffline: false,

  access: null,
  accessChecked: false,
  accessError: null,
  accessLoading: false,
};

const safeStorageSet = (key, value) => {
  if (typeof localStorage !== 'undefined' && value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.warn(`Failed to save ${key}:`, e);
    }
  }
};

const safeStorageRemove = (key) => {
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn(`Failed to remove ${key}:`, e);
    }
  }
};

const normalizeCharacters = (characters) => {
  if (!Array.isArray(characters)) return [];
  return characters.map((c) => {
    if (typeof c === 'string') {
      return {
        character_id: c,
        image_bit_id: c,
        name: null,
        source: 'created',
        status: 'design',
        added_at: null,
        status_updated_at: null,
      };
    }
    return c;
  });
};

const authSlice = createSlice({
  name: 'auth',
  initialState: initialState,

  reducers: {
    clearError: (state) => {
      state.error = null;
    },

    clearAccessError: (state) => {
      state.accessError = null;
    },

    resetAuth: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.sessionId = null;
      state.token = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isLoading = false;
      state.error = null;
      state.authChecked = false;
      state.connectionStatus = null;
      state.isOffline = false;
      state.access = null;
      state.accessChecked = false;
      state.accessError = null;
      state.accessLoading = false;

      safeStorageRemove('access_token');
      safeStorageRemove('refresh_token');
      safeStorageRemove('auth_token');
      safeStorageRemove('game_auth_token');
      safeStorageRemove('selected_character_id');
      safeStorageRemove('character_data');
      safeStorageRemove('auth_user');
      safeStorageRemove('auth_time');
      safeStorageRemove('auth_session');
    },

    setToken: (state, action) => {
      const token = action.payload;
      state.token = token;

      const isJWT = token && typeof token === 'string' && token.split('.').length === 3;

      if (isJWT) {
        state.accessToken = token;
        safeStorageSet('access_token', token);
      } else {
        state.accessToken = null;
      }

      safeStorageSet('auth_token', token);
    },

    setTokens: (state, action) => {
      const { accessToken, refreshToken } = action.payload;

      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.token = accessToken;

      if (accessToken) {
        safeStorageSet('access_token', accessToken);
        safeStorageSet('auth_token', accessToken);
      }
      if (refreshToken) {
        safeStorageSet('refresh_token', refreshToken);
      }
    },

    restoreTokens: (state) => {
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      const authToken = localStorage.getItem('auth_token');
      const gameAuthToken = localStorage.getItem('game_auth_token');
      const sessionId = localStorage.getItem('auth_session');

      if (sessionId) {
        state.sessionId = sessionId;
      }

      const finalAccessToken = authToken || accessToken;

      if (finalAccessToken) {
        const isJWT =
          finalAccessToken &&
          typeof finalAccessToken === 'string' &&
          finalAccessToken.split('.').length === 3;

        if (isJWT) {
          state.accessToken = finalAccessToken;
        } else {
          state.accessToken = null;
        }
        state.token = finalAccessToken;
      }

      if (refreshToken) {
        state.refreshToken = refreshToken;
      }

      if (gameAuthToken) {
        state.gameAuthToken = gameAuthToken;
      }
    },

    updateUser: (state, action) => {
      state.user = {
        ...state.user,
        ...action.payload,
      };

      if (state.user) {
        try {
          const currentUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
          safeStorageSet('auth_user', JSON.stringify({ ...currentUser, ...action.payload }));
        } catch (error) {
          console.warn('Failed to update user in localStorage:', error);
        }
      }
    },

    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    setOffline: (state, action) => {
      state.isOffline = action.payload;
    },

    updateToken: (state, action) => {
      const token = action.payload;
      state.token = token;

      const isJWT = token && typeof token === 'string' && token.split('.').length === 3;

      if (isJWT) {
        state.accessToken = token;
        safeStorageSet('access_token', token);
      } else {
        state.accessToken = null;
      }

      safeStorageSet('auth_token', token);
    },

    updateRefreshToken: (state, action) => {
      state.refreshToken = action.payload;
      safeStorageSet('refresh_token', action.payload);
    },

    setGameAuthToken: (state, action) => {
      state.gameAuthToken = action.payload;
      safeStorageSet('game_auth_token', action.payload);
    },

    setSessionId: (state, action) => {
      state.sessionId = action.payload;
      safeStorageSet('auth_session', action.payload);
    },

    setAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload;
    },

    setUser: (state, action) => {
      state.user = action.payload;
      if (state.user) {
        safeStorageSet('auth_user', JSON.stringify(state.user));
      }
    },

    clearGameAuthToken: (state) => {
      state.gameAuthToken = null;
      safeStorageRemove('game_auth_token');
    },

    updateCharacterInAccess: (state, action) => {
      if (!state.access || !Array.isArray(state.access.characters)) return;
      const { characterId, updates } = action.payload;
      if (!characterId || !updates) return;

      const idx = state.access.characters.findIndex(
        (c) => c.character_id === characterId
      );
      if (idx !== -1) {
        state.access.characters[idx] = {
          ...state.access.characters[idx],
          ...updates,
        };
      }
    },

    clearAccess: (state) => {
      state.access = null;
      state.accessChecked = false;
      state.accessError = null;
      state.accessLoading = false;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(checkAuthStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.identity || action.payload.user;
        state.sessionId = action.payload.session?.id || null;
        state.authChecked = true;
        state.error = null;

        const token = action.payload.token || action.payload.accessToken;
        if (token) {
          state.token = token;
          const isJWT =
            token && typeof token === 'string' && token.split('.').length === 3;
          if (isJWT) {
            state.accessToken = token;
          }
          safeStorageSet('access_token', token);
          safeStorageSet('auth_token', token);
        }

        if (state.user) {
          safeStorageSet('auth_user', JSON.stringify(state.user));
        }

        if (state.sessionId) {
          safeStorageSet('auth_session', state.sessionId);
        }

        safeStorageSet('auth_time', Date.now().toString());
      })
      .addCase(checkAuthStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.sessionId = null;
        state.token = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.authChecked = true;
        state.error = action.payload === null ? null : action.payload;

        state.access = null;
        state.accessChecked = false;
        state.accessError = null;

        safeStorageRemove('access_token');
        safeStorageRemove('refresh_token');
        safeStorageRemove('auth_token');
        safeStorageRemove('auth_user');
        safeStorageRemove('auth_time');
        safeStorageRemove('auth_session');
      })

      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.identity || action.payload.user;
        state.sessionId = action.payload.session?.id || null;
        state.error = null;

        const token = action.payload.token || action.payload.accessToken;
        if (token) {
          state.token = token;
          const isJWT =
            token && typeof token === 'string' && token.split('.').length === 3;
          if (isJWT) {
            state.accessToken = token;
          }
          safeStorageSet('access_token', token);
          safeStorageSet('auth_token', token);
        }

        if (state.user) {
          safeStorageSet('auth_user', JSON.stringify(state.user));
        }

        if (state.sessionId) {
          safeStorageSet('auth_session', state.sessionId);
        }

        safeStorageSet('auth_time', Date.now().toString());
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.sessionId = null;
        state.token = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.error = action.payload;

        state.access = null;
        state.accessChecked = false;
        state.accessError = null;

        safeStorageRemove('access_token');
        safeStorageRemove('refresh_token');
        safeStorageRemove('auth_token');
        safeStorageRemove('auth_user');
        safeStorageRemove('auth_time');
        safeStorageRemove('auth_session');
      })

      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.identity || action.payload.user;
        state.sessionId = action.payload.session?.id || null;
        state.error = null;

        const token = action.payload.token || action.payload.accessToken;
        if (token) {
          state.token = token;
          const isJWT =
            token && typeof token === 'string' && token.split('.').length === 3;
          if (isJWT) {
            state.accessToken = token;
          }
          safeStorageSet('access_token', token);
          safeStorageSet('auth_token', token);
        }

        if (state.user) {
          safeStorageSet('auth_user', JSON.stringify(state.user));
        }

        if (state.sessionId) {
          safeStorageSet('auth_session', state.sessionId);
        }

        safeStorageSet('auth_time', Date.now().toString());
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.token = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.error = action.payload;

        state.access = null;
        state.accessChecked = false;
        state.accessError = null;

        safeStorageRemove('access_token');
        safeStorageRemove('refresh_token');
        safeStorageRemove('auth_token');
        safeStorageRemove('auth_user');
        safeStorageRemove('auth_time');
        safeStorageRemove('auth_session');
      })

      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.sessionId = null;
        state.token = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.error = null;
        state.gameAuthToken = null;
        state.access = null;
        state.accessChecked = false;
        state.accessError = null;
        state.accessLoading = false;

        safeStorageRemove('access_token');
        safeStorageRemove('refresh_token');
        safeStorageRemove('auth_token');
        safeStorageRemove('auth_user');
        safeStorageRemove('auth_time');
        safeStorageRemove('auth_session');
        safeStorageRemove('game_auth_token');
        safeStorageRemove('selected_character_id');
        safeStorageRemove('character_data');
      })
      .addCase(logoutUser.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.sessionId = null;
        state.token = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.error = null;
        state.gameAuthToken = null;
        state.access = null;
        state.accessChecked = false;
        state.accessError = null;
        state.accessLoading = false;

        safeStorageRemove('access_token');
        safeStorageRemove('refresh_token');
        safeStorageRemove('auth_token');
        safeStorageRemove('auth_user');
        safeStorageRemove('auth_time');
        safeStorageRemove('auth_session');
        safeStorageRemove('game_auth_token');
        safeStorageRemove('selected_character_id');
        safeStorageRemove('character_data');
      })

      .addCase(diagnoseConnection.pending, (state) => {
        state.connectionStatus = { status: 'checking' };
      })
      .addCase(diagnoseConnection.fulfilled, (state, action) => {
        state.connectionStatus = action.payload;
      })
      .addCase(diagnoseConnection.rejected, (state, action) => {
        state.connectionStatus = {
          status: 'error',
          error: action.payload,
        };
      })

      .addCase(refreshSession.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(refreshSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.identity || action.payload.user;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(refreshSession.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload;
      })

      .addCase(fetchAccess.pending, (state) => {
        state.accessLoading = true;
        state.accessError = null;
      })
      .addCase(fetchAccess.fulfilled, (state, action) => {
        state.accessLoading = false;

        const rawAccess = action.payload || {};
        const normalizedCharacters = normalizeCharacters(rawAccess.characters);

        state.access = {
          ...rawAccess,
          characters: normalizedCharacters,
        };
        state.accessChecked = true;
        state.accessError = null;

        if (rawAccess) {
          state.user = {
            ...state.user,
            ...(rawAccess.email && { email: rawAccess.email }),
            ...(rawAccess.display_name && { name: rawAccess.display_name }),
            id: rawAccess.kratos_identity_id || state.user?.id,
            system_role: rawAccess.system_role,
            player_id: rawAccess.player_id,
          };
        }

        console.log('🎭 Access profile updated in Redux:', {
          role: rawAccess?.system_role,
          charactersCount: normalizedCharacters.length,
        });
      })
      .addCase(fetchAccess.rejected, (state, action) => {
        state.accessLoading = false;
        state.accessChecked = true;

        const err = action.payload;
        if (err?.status === 401) {
          state.access = null;
          state.accessError = null;
        } else if (err?.status === 404) {
          state.accessError =
            'Access endpoint not available. Restart imcon service.';
          console.warn('⚠️ /api/access/me not registered — restart imcon');
        } else {
          state.accessError = err?.message || 'Failed to fetch access';
        }
      })

      .addCase(emancipateCharacter.pending, (state) => {
        state.accessLoading = true;
      })
      .addCase(emancipateCharacter.fulfilled, (state, action) => {
        state.accessLoading = false;
        console.log('🎗️ Character emancipated:', action.payload?.character_id);
      })
      .addCase(emancipateCharacter.rejected, (state, action) => {
        state.accessLoading = false;
        state.accessError = action.payload?.message || 'Failed to emancipate';
        console.error('❌ Emancipation failed:', action.payload);
      })

      .addCase(registerCharacterInAccess.pending, (state) => {
        // silent
      })
      .addCase(registerCharacterInAccess.fulfilled, (state, action) => {
        console.log('📝 Character registered:', action.payload?.character_id);
      })
      .addCase(registerCharacterInAccess.rejected, (state, action) => {
        console.warn('⚠️ Failed to register character:', action.payload);
      })

      .addCase(fetchCharacters.pending, (state) => {
        // silent
      })
      .addCase(fetchCharacters.fulfilled, (state, action) => {
        if (state.access) {
          state.access.characters = normalizeCharacters(action.payload);
        }
      })
      .addCase(fetchCharacters.rejected, (state, action) => {
        console.warn('⚠️ fetchCharacters failed:', action.payload);
      });
  },
});

export const {
  clearError,
  clearAccessError,
  resetAuth,
  setToken,
  setTokens,
  restoreTokens,
  updateUser,
  setLoading,
  setOffline,
  updateToken,
  updateRefreshToken,
  setGameAuthToken,
  setSessionId,
  setAuthenticated,
  setUser,
  clearGameAuthToken,
  updateCharacterInAccess,
  clearAccess,
} = authSlice.actions;

export const selectAuth = (state) => state.auth;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.user;
export const selectSessionId = (state) => state.auth.sessionId;
export const selectToken = (state) => state.auth.token;
export const selectAccessToken = (state) => state.auth.accessToken;
export const selectRefreshToken = (state) => state.auth.refreshToken;
export const selectGameAuthToken = (state) => state.auth.gameAuthToken;
export const selectAuthLoading = (state) => state.auth.isLoading;
export const selectAuthError = (state) => state.auth.error;
export const selectAuthChecked = (state) => state.auth.authChecked;
export const selectConnectionStatus = (state) => state.auth.connectionStatus;
export const selectIsOffline = (state) => state.auth.isOffline;

export const selectAccess = (state) => state.auth.access;
export const selectAccessChecked = (state) => state.auth.accessChecked;
export const selectAccessError = (state) => state.auth.accessError;
export const selectAccessLoading = (state) => state.auth.accessLoading;
export const selectSystemRole = (state) => state.auth.access?.system_role || 'user';
export const selectCharacters = (state) => state.auth.access?.characters || [];
export const selectWallet = (state) =>
  state.auth.access?.wallet || { balance: 0, currency: 'uef_credits' };

export default authSlice.reducer;
